import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { apiService } from '../services/api';
import { sendAgentTurn, subscribeAnalysisEvents, stopAgentTurn } from '../services/agentClient';
import { useToast } from '../context/ToastContext';

import TokenUsageModal from '../components/TokenUsageModal';
import ArtifactsRail from '../design-system/chat/ArtifactsRail';
import TopBar from '../design-system/chat/TopBar';
import EmptyState from '../design-system/chat/EmptyState';
import UserMessage from '../design-system/chat/UserMessage';
import AgentMessage from '../design-system/chat/AgentMessage';
import Composer, { SuggestionChip } from '../design-system/chat/Composer';
import BlockRenderer from '../design-system/chat-blocks/BlockRenderer';
import { isHiddenError } from '../design-system/chat-blocks/ErrorBlock';
import TextBubble from '../design-system/chat-blocks/TextBubble';
import { formatMetricNumber, metricMeta } from '../design-system/chat-blocks/metricTone';
import DatasetViewer from '../design-system/chat/DatasetViewer';
import ConnectSourceModal from '../components/warehouse/ConnectSourceModal';
import Popover from '../design-system/primitives/Popover';
import ManagePipelinePanel from '../components/pipeline/ManagePipelinePanel';
import ShareAnalysisPanel from '../components/sharing/ShareAnalysisPanel';
import { cn } from '../components/ui/cn';
import '../design-system/chat/chat.css';

const TMP_PREFIX = 'tmp-';

// Subtitle for a saved model in the artifacts rail. Scale and label come from the
// shared metric registry, so a user-selected training metric (rmse, roc_auc, …)
// reads correctly here too; only the abbreviations are local, as this line is
// tight on space.
const METRIC_ABBREV = { accuracy: 'Acc', balanced_accuracy: 'Bal. acc' };

// Same format the start page's AnalysisCard uses, e.g. "Sep 11, 2026".
const formatTrainedOn = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const formatMetric = (name, value) => {
  if (value == null) return null;
  return `${METRIC_ABBREV[(name || '').toLowerCase()] || metricMeta(name).label} `
    + `${formatMetricNumber(value, name)}`;
};

// Phase order mirrors backend PHASE_SEQUENCE — used to detect jump-backs.
// Where a dataset group came from: the first raw upload imported from a
// warehouse connector (profile_data.warehouse_source), or null for files.
const warehouseSource = (raws) => {
  const meta = raws.map(r => r.profile_data?.warehouse_source).find(Boolean);
  return meta ? { type: meta.connection_type, connectionId: meta.connection_id, table: meta.table } : null;
};

const PHASE_SEQUENCE = [
  '1_understand', '2_1_classify', '2_2_preprocess', '2_3_clean',
  '2_4_analyze', '3_model', '4_results', '5_explain', '6_insights',
];

// Return stepHistory with all entries at or after `phase` removed.
function trimStepHistory(prev, phase) {
  const idx = PHASE_SEQUENCE.indexOf(phase);
  if (idx < 0) return prev;
  return prev.filter(e => PHASE_SEQUENCE.indexOf(e.phase) < idx);
}

function isTextOnly(msg) {
  return msg.role === 'assistant' && (!msg.payload || msg.payload.block_type === 'text');
}

// Mirrors the thread's render branches: true when a message produces a visible turn.
function rendersInThread(msg) {
  if (isHiddenError(msg.payload)) return false;
  if (msg.role === 'user') return true;
  if (isTextOnly(msg) && msg.content) return true;
  return !!(msg.payload && msg.payload.block_type);
}

// Role of the closest earlier message that is actually shown, so hidden rows
// never split (or join) a run of agent turns.
function prevShownRole(messages, i) {
  for (let j = i - 1; j >= 0; j -= 1) {
    if (rendersInThread(messages[j])) return messages[j].role === 'user' ? 'user' : 'agent';
  }
  return null;
}

// Recovered "tool not offered in this phase" cards. The model self-corrects;
// the red card is noise (and a demo leak). Real warehouse/SQL errors still render.
const HIDE_BRANDING = process.env.REACT_APP_HIDE_BRANDING === 'true';

function isDemoHiddenError(msg) {
  if (!HIDE_BRANDING) return false;
  if (msg?.payload?.block_type === 'error') return true;
  return msg?.role === 'tool' && msg?.status === 'error';
}

function patchMessage(messages, message_id, patch) {
  return messages.map(m => (m.id === message_id ? { ...m, ...patch } : m));
}

const ChatAnalysisPage = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isNewAnalysis = routeId === 'new';
  const [analysisId, setAnalysisId] = useState(isNewAnalysis ? null : routeId);

  const [analysis, setAnalysis] = useState(isNewAnalysis ? {
    name: 'Untitled Analysis', datasets: [], user_context: '',
  } : null);
  const [loading, setLoading] = useState(!isNewAnalysis);
  const [messages, setMessages] = useState([]);
  const [composerValue, setComposerValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [viewerState, setViewerState] = useState(null); // { groupId, initialView }
  // Files selected via drop or the attach button — staged in the composer
  // until the user clicks Send. Each entry: { id, file }.
  const [pendingFiles, setPendingFiles] = useState([]);
  // Phase-graph state — updated by agent.phase_started and agent.phase_completed SSE events.
  const [agentPhase, setAgentPhaseLabel] = useState(null); // current phase label (display)
  // The phase *id* (e.g. '2_2_preprocess') drives the activity animation, which
  // must not key off the human label. Both move through one setter so they can
  // never desync across the ~10 places that update the phase; the id defaults
  // to null so clears and connection notices drop the animation automatically.
  const [agentPhaseId, setAgentPhaseId] = useState(null);
  const setAgentPhase = useCallback((label, id = null) => {
    setAgentPhaseLabel(label);
    setAgentPhaseId(id);
  }, []);
  // Progress notes from a long-running tool ("waiting for a free training
  // slot") happen *inside* a phase, so they only retitle it — routing them
  // through setAgentPhase would clear the id and drop the activity animation
  // mid-training.
  const setAgentStatus = useCallback((label) => setAgentPhaseLabel(label), []);
  const [stepHistory, setStepHistory] = useState([]);     // [{phase, label, timestamp, detail}]
  // The clean phase (2_3) as seen by this turn: when it started and whether it
  // finished. The "Cleaning your data" block paces its bar off this, because
  // the backend persists that block at 100% before the phase even runs.
  // null until the phase starts; cleared when the turn ends (see below).
  const [cleanPhase, setCleanPhase] = useState(null); // { startedAt, done }
  const trackCleanPhase = useCallback((type, data) => {
    if (data?.phase !== '2_3_clean') return;
    if (type === 'agent.phase_started') setCleanPhase({ startedAt: Date.now(), done: false });
    else if (type === 'agent.phase_completed') setCleanPhase(prev => (prev ? { ...prev, done: true } : { startedAt: null, done: true }));
  }, []);
  const [agentStopped, setAgentStopped] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  // /events/ replay cursor from the GET-history snapshot. The subscription
  // waits for it (null = history not fetched yet) so past turns aren't
  // replayed into the chat on every page load. 0 = new analysis, no events.
  const [eventCursor, setEventCursor] = useState(isNewAnalysis ? 0 : null);
  const [notebookExporting, setNotebookExporting] = useState(false);
  const [warehouseOpen, setWarehouseOpen] = useState(false);
  // Connection the modal opens straight into (a Sources row), or null for its list.
  const [warehouseConnId, setWarehouseConnId] = useState(null);
  const [connections, setConnections] = useState([]);
  const [pipelineAnchor, setPipelineAnchor] = useState(null);
  const [shareAnchor, setShareAnchor] = useState(null);

  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const hasNamedRef = useRef(false);
  // Prevent the agent.file_uploaded auto-trigger from firing more than once,
  // and from firing on analyses that already have a conversation.
  const hasAutoTriggeredRef = useRef(false);
  // True once the initial history fetch has settled (or immediately for /new
  // where there is no history to load). Guards against a race where the SSE
  // channel replays the old agent.file_uploaded event before getAgentChatHistory
  // returns and sets hasAutoTriggeredRef — without this, reloading a paused
  // analysis would re-trigger the agent automatically.
  const initialLoadCompleteRef = useRef(isNewAnalysis);
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Register window.cost() for token usage inspection — only in debug mode or for staff users.
  useEffect(() => {
    if (!analysis?.cost_console_enabled) return;
    window.cost = async () => {
      if (analysisId) {
        try {
          const resp = await apiService.getAnalysis(analysisId);
          setAnalysis(resp.data);
        } catch (_) { /* non-fatal — show whatever is in state */ }
      }
      setShowCostModal(true);
    };
    return () => { delete window.cost; };
  }, [analysisId, analysis?.cost_console_enabled]);

  // Lazy-create the analysis row on first action when route is /new.
  const ensureAnalysisExists = useCallback(async () => {
    if (analysisId) return analysisId;
    const resp = await apiService.createAnalysis({
      name: analysis?.name || 'Untitled Analysis',
      description: analysis?.description || '',
    });
    const newId = resp.data.id;
    setAnalysis(resp.data);
    setAnalysisId(newId);
    // Use replaceState instead of navigate() so the URL updates without
    // triggering React Router's key-based remount of this component.
    // A navigate() call changes the route id from 'new' → real id, which
    // causes KeyedChatAnalysisPage to swap in a fresh component instance,
    // resetting all state (including busy=true) and hiding the Thinking bubble.
    window.history.replaceState(null, '', `/app/analysis/${newId}`);
    return newId;
  }, [analysisId, analysis]);

  // Auto-scroll on new messages or when the agent goes busy. Returning to the
  // tab refetches the history into a fresh but identical array, so key on the
  // conversation's tail rather than the array identity to leave the reader's
  // scroll position alone.
  const scrollKeyRef = useRef(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    const key = `${messages.length}:${last?.id ?? ''}:${busy}`;
    if (key === scrollKeyRef.current) return;
    scrollKeyRef.current = key;
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  // Tool errors a staff viewer receives in production are flagged console_only
  // by the server (block_redaction) and never rendered; surface them here, once
  // per message, so this covers both the history load and live frames.
  const consoleLoggedErrorsRef = useRef(new Set());
  useEffect(() => {
    messages.forEach((m) => {
      const bd = m.payload?.block_data;
      if (m.payload?.block_type !== 'error' || !bd?.console_only) return;
      if (consoleLoggedErrorsRef.current.has(m.id)) return;
      consoleLoggedErrorsRef.current.add(m.id);
      console.warn(`[qombra] tool error · ${bd.tool || m.tool_name || 'unknown'}: ${bd.message}`, bd);
    });
  }, [messages]);

  // Initial load: analysis metadata + chat history (only for existing analyses).
  // Toast/navigate refs change on every parent re-render (ToastProvider's value
  // object isn't memoized) so we exclude them from deps to avoid a fetch loop.
  useEffect(() => {
    if (!analysisId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [analysisResp, chatResp] = await Promise.all([
          apiService.getAnalysis(analysisId),
          apiService.getAgentChatHistory(analysisId).catch(() => ({ data: { messages: [] } })),
        ]);
        if (cancelled) return;
        setAnalysis(analysisResp.data);
        const history = (chatResp.data && chatResp.data.messages) || [];
        setMessages(history);
        // Prevent the file_uploaded auto-trigger from firing on reload when
        // the analysis already has a conversation.
        if (history.length > 0) hasAutoTriggeredRef.current = true;
        initialLoadCompleteRef.current = true;
        // A turn is still running server-side (page reload mid-turn):
        // restore the busy indicator; /events/ delivers the rest of the turn.
        if (chatResp.data && chatResp.data.active_turn_id) setBusy(true);
        setEventCursor((chatResp.data && chatResp.data.last_event_id) || 0);
      } catch (e) {
        if (e?.response?.status === 403) {
          toast(e.response?.data?.detail || 'You do not have access to this analysis.', 'error');
          navigate('/app', { replace: true });
        } else {
          toast('Failed to load analysis.', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  // Refetch analysis metadata after a state-changing event (new dataset, model, predictions).
  // Used by both the SSE channel (preprocessing complete) and tool_message handler.
  const refreshAnalysis = useCallback(async () => {
    if (!analysisId) return;
    try {
      const resp = await apiService.getAnalysis(analysisId);
      setAnalysis(resp.data);
    } catch (e) {
      // Non-fatal — the rail just stays slightly stale until next page load.
    }
  }, [analysisId]);

  // True while a turn is running server-side WITHOUT a live POST stream
  // attached (stream dropped mid-turn, or we got a 409 and attached to an
  // already-running turn). While set, /events/ is the delivery channel and
  // busy must not be cleared by POST-stream housekeeping.
  const reconnectingRef = useRef(false);

  // Shared handler for agent-turn events. Both channels feed it — the live
  // POST stream and the durable /events/ channel (which replays the same
  // events after a reconnect) — so every message append dedupes by id.
  const handleTurnEvent = useCallback((type, data) => {
    if (type === 'message_start') {
      setAgentStopped(false);
      setBusy(true);
    } else if (type === 'assistant_message') {
      setMessages(prev => prev.some(m => m.id === data.message_id) ? prev : [...prev, {
        id: data.message_id,
        role: 'assistant',
        content: data.content,
        payload: data.payload || (data.content ? { block_type: 'text', block_data: { text: data.content } } : null),
        status: 'complete',
        turn_id: data.turn_id,
      }]);
    } else if (type === 'tool_message') {
      setMessages(prev => prev.some(m => m.id === data.message_id) ? prev : [...prev, {
        id: data.message_id,
        role: 'tool',
        content: '',
        tool_name: data.tool_name,
        status: data.status,
        payload: { block_type: data.block_type, block_data: data.block_data },
        artifact_refs: data.artifact_refs || [],
        turn_id: data.turn_id,
      }]);
      if (data.status === 'complete' && [
        'train_preliminary_model', 'train_full_model', 'train_models', 'run_inference',
      ].includes(data.tool_name)) {
        refreshAnalysis();
      }
    } else if (type === 'agent.phase_started') {
      setAgentPhase(data.label || data.phase, data.phase);
      trackCleanPhase(type, data);
    } else if (type === 'agent.phase_completed') {
      trackCleanPhase(type, data);
      setStepHistory(prev => {
        // Trim any steps at or after this phase (handles jump-back via jump_to_phase).
        const trimmed = trimStepHistory(prev, data.phase);
        // Avoid duplicate when both channels deliver the same event.
        if (trimmed.some(e => e.phase === data.phase && e.timestamp === data.timestamp)) return trimmed;
        return [...trimmed, { phase: data.phase, label: data.label, timestamp: data.timestamp, detail: data.detail || {} }];
      });
      // Refresh the analysis object so new artifacts appear in the rail
      // without requiring a page reload.
      if (['2_2_preprocess', '2_3_clean', '3_model', '4_results'].includes(data.phase)) {
        refreshAnalysis();
      }
    } else if (type === 'agent.status') {
      // e.g. "waiting for a free training slot" — reuse the phase indicator.
      // Some statuses carry a console-only reason (e.g. the predictive engine
      // warming up) that must never be rendered to the user.
      if (data && data.debug) console.log('[qombra]', data.debug);
      if (data && data.message) setAgentStatus(data.message);
    } else if (type === 'message_complete') {
      reconnectingRef.current = false;
      setBusy(false);
      setAgentPhase(null);
      if (data && data.stopped) setAgentStopped(true);
    } else if (type === 'error') {
      if (!HIDE_BRANDING && data.code === 'output_token_limit_reached') toast(data.message, 'error');
      if (!HIDE_BRANDING) {
        // Key on turn_id so the same error replayed on the second channel
        // doesn't render a second bubble.
        const errId = `${TMP_PREFIX}err-${data.turn_id || Date.now()}`;
        setMessages(prev => prev.some(m => m.id === errId) ? prev : [...prev, {
          id: errId,
          role: 'tool',
          content: '',
          status: 'error',
          payload: { block_type: 'error', block_data: { message: data.message } },
        }]);
      }
      reconnectingRef.current = false;
      setBusy(false);
      setAgentPhase(null);
    } else if (type === 'stream_disconnected') {
      // The live POST stream dropped, but the turn keeps running server-side.
      // Stay busy and let the /events/ channel (auto-reconnecting, with
      // replay) deliver the rest of the turn.
      reconnectingRef.current = true;
      setAgentPhase('Reconnecting…');
      toast('Connection lost — reconnecting…', 'info');
    } else if (type === 'turn_in_progress') {
      // POST was rejected with 409: a turn is already running. Attach to it —
      // /events/ delivers its progress. Mark the turn as continuing outside
      // this POST stream so callsite safety nets don't clear busy.
      reconnectingRef.current = true;
      setBusy(true);
      setAgentStopped(false);
      toast('The agent is still working on the previous request.', 'info');
    }
    // toast is intentionally excluded from deps — see initial-load effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAnalysis]);

  // Subscribe to OOB events (preprocessing progress, turn events, etc.).
  useEffect(() => {
    if (!analysisId) return undefined;  // Skip when on /new — nothing to subscribe to yet.
    if (eventCursor === null) return undefined;  // Wait for the history snapshot's cursor.
    const unsub = subscribeAnalysisEvents(analysisId, (topic, data) => {
      if (topic === 'chat.message' && data && data.message_id) {
        // Backend sends `message_id` (no `id`); normalize so subsequent
        // chat.message.update events can find and patch this row by `m.id`.
        setMessages(prev => prev.some(m => m.id === data.message_id)
          ? prev
          : [...prev, { ...data, id: data.message_id }]);
      } else if (topic === 'chat.message.update' && data && data.message_id) {
        setMessages(prev => {
          // If we missed the original chat.message event (e.g., SSE attached
          // after the bubble was created), append the message from the patch
          // payload so the UI still renders it.
          if (!prev.some(m => m.id === data.message_id)) {
            return [...prev, {
              id: data.message_id,
              role: 'assistant',
              content: data.content || '',
              payload: data.payload || null,
              status: data.status || 'complete',
              artifact_refs: data.artifact_refs || [],
            }];
          }
          return patchMessage(prev, data.message_id, {
            payload: data.payload ?? undefined,
            status: data.status ?? undefined,
            content: data.content ?? undefined,
          });
        });
      } else if (topic === 'preprocessing.complete') {
        refreshAnalysis();
      } else if ([
        // Turn events also flow over this durable channel (the backend
        // dual-writes them), which is what lets a reconnecting client catch
        // up on a turn whose live POST stream died. handleTurnEvent dedupes.
        'message_start', 'assistant_message', 'tool_message',
        'agent.phase_started', 'agent.phase_completed',
        'agent.status', 'message_complete', 'error',
      ].includes(topic) && data) {
        handleTurnEvent(topic, data);
      } else if (topic === 'agent.stopped') {
        setAgentStopped(true);
        setBusy(false);
      } else if (topic === 'agent.file_uploaded' && data) {
        // Auto-trigger Turn 1 only once and only when no conversation exists yet.
        // Also wait for the initial history fetch to complete — without this guard
        // the replayed SSE event can arrive before getAgentChatHistory returns,
        // making hasAutoTriggeredRef and messagesRef look like a fresh analysis
        // and causing the agent to re-run on every page reload.
        if (!initialLoadCompleteRef.current) return;
        if (hasAutoTriggeredRef.current || messagesRef.current.length > 0) return;
        hasAutoTriggeredRef.current = true;
        // Small timeout to let the UI settle after the upload response.
        setTimeout(() => {
          const activeId = analysisId || data.analysis_id;
          if (!activeId) return;
          setBusy(true);
          sendAgentTurn(activeId, { autoStart: true }, handleTurnEvent)
            .then(() => { if (!reconnectingRef.current) setBusy(false); })
            .catch(() => { if (!reconnectingRef.current) setBusy(false); });
        }, 300);
      }
    }, { initialLastEventId: eventCursor });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId, eventCursor, refreshAnalysis, handleTurnEvent]);

  // Auto-name the analysis from the first user message (fire-and-forget).
  const autoNameAnalysis = useCallback((activeId, text) => {
    if (!text || hasNamedRef.current) return;
    hasNamedRef.current = true;
    apiService.generateAnalysisName(activeId, text)
      .then(resp => {
        const newName = resp.data?.name;
        if (newName && newName !== 'Untitled Analysis') {
          setAnalysis(prev => ({ ...prev, name: newName }));
          apiService.updateAnalysis(activeId, { name: newName }).catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  const handleSend = useCallback(async (overrideText) => {
    const text = ((overrideText ?? composerValue) || '').trim();
    const filesToSend = pendingFiles;
    if ((!text && filesToSend.length === 0) || busy) return;
    const isFirstMessage = messages.length === 0;

    // If any files are staged, this turn is an upload — push the user's typed
    // text + filenames as the user bubble, then call upload_and_clean which
    // emits the assistant "preprocessing_progress" bubble itself via SSE.
    if (filesToSend.length > 0) {
      const tmpUserId = `${TMP_PREFIX}user-${Date.now()}`;
      const attachments = filesToSend.map(p => ({ name: p.file.name }));
      setMessages(prev => [...prev, {
        id: tmpUserId, role: 'user', content: text,
        payload: { attachments }, status: 'complete',
      }]);
      setComposerValue('');
      setPendingFiles([]);
      setBusy(true);

      let activeId;
      try {
        activeId = await ensureAnalysisExists();
        if (isFirstMessage) autoNameAnalysis(activeId, text);
      } catch (e) {
        toast(e?.response?.data?.detail || 'Could not create analysis.', 'error');
        setBusy(false);
        return;
      }
      let agentStarted = false;
      try {
        const resp = await apiService.uploadAndClean(activeId, filesToSend.map(p => p.file), text);
        if (resp?.data?.user_message_id) {
          setMessages(prev => patchMessage(prev, tmpUserId, { id: resp.data.user_message_id }));
        }
        // Raw Dataset rows are persisted by uploadAndClean — refresh immediately
        // so the rail shows the uploaded files before the agent starts processing.
        refreshAnalysis();
        // Start agent turn directly — don't wait for agent.file_uploaded SSE,
        // which may miss the event if the SSE connection isn't established yet
        // (this happens on /new where the subscription starts after ensureAnalysisExists).
        hasAutoTriggeredRef.current = true;
        agentStarted = true;
        // busy remains true through the agent turn
        sendAgentTurn(activeId, { autoStart: true }, handleTurnEvent)
          .catch(() => { if (!reconnectingRef.current) setBusy(false); });
      } catch (e) {
        toast(e?.response?.data?.detail || 'Upload failed.', 'error');
      } finally {
        if (!agentStarted) setBusy(false);
      }
      return;
    }

    const tmpUserId = `${TMP_PREFIX}user-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: tmpUserId, role: 'user', content: text, payload: null, status: 'complete',
    }]);
    setComposerValue('');
    setBusy(true);

    let activeId;
    try {
      activeId = await ensureAnalysisExists();
      if (isFirstMessage) autoNameAnalysis(activeId, text);
    } catch (e) {
      toast(e?.response?.data?.detail || 'Could not create analysis.', 'error');
      setBusy(false);
      return;
    }

    try {
      await sendAgentTurn(activeId, { text }, (type, data) => {
        if (type === 'message_start') {
          // Replace the optimistic temp id with the persisted user_message_id.
          setMessages(prev => patchMessage(prev, tmpUserId, { id: data.user_message_id }));
        } else if (type === 'turn_in_progress') {
          // 409: the previous turn is still running — this message was NOT
          // accepted. Withdraw the optimistic bubble and put the text back.
          setMessages(prev => prev.filter(m => m.id !== tmpUserId));
          setComposerValue(text);
        }
        handleTurnEvent(type, data);
      });
    } catch (e) {
      // sendAgentTurn converts stream errors to stream_disconnected events, so
      // reaching here means an unexpected throw before the stream started
      // (e.g. network refused). The turn may not have started — reset.
      setBusy(false);
      setAgentPhase(null);
      toast('Could not reach the server. Please try again.', 'error');
    }
    // If the stream closed without an explicit message_complete and we're not
    // waiting for /events/ to finish the turn, clear busy as a safety net.
    if (!reconnectingRef.current) setBusy(false);
  }, [busy, composerValue, pendingFiles, messages, ensureAnalysisExists, autoNameAnalysis, toast, handleTurnEvent, refreshAnalysis, setAgentPhase]);

  // Drop / attach-button → stage files in the composer. They aren't uploaded
  // until the user clicks Send (so they can type business context first).
  const handleFileChosen = useCallback((filesArg) => {
    const files = Array.isArray(filesArg) ? filesArg : (filesArg ? [filesArg] : []);
    if (files.length === 0) return;
    setPendingFiles(prev => [
      ...prev,
      ...files.map(f => ({ id: `${f.name}-${f.size}-${f.lastModified}`, file: f })),
    ]);
  }, []);

  const handleRemoveAttached = useCallback((id) => {
    setPendingFiles(prev => prev.filter(p => p.id !== id));
  }, []);

  // Warehouse import finished server-side (raw Dataset created + user bubble
  // persisted) — mirror the upload flow: show the bubble, refresh the rail,
  // and start the agent turn directly (same reasoning as uploadAndClean above:
  // don't rely on the agent.file_uploaded SSE event).
  const handleWarehouseImported = useCallback((respData, meta) => {
    setWarehouseOpen(false);
    const activeId = meta?.analysisId || analysisId;
    const attachments = (respData.file_names || []).map(name => ({ name }));
    setMessages(prev => [...prev, {
      id: respData.user_message_id || `${TMP_PREFIX}user-${Date.now()}`,
      role: 'user', content: '',
      payload: { attachments }, status: 'complete',
    }]);
    setBusy(true);
    refreshAnalysis();
    hasAutoTriggeredRef.current = true;
    sendAgentTurn(activeId, { autoStart: true }, (type, evData) => {
      if (type === 'assistant_message') {
        setMessages(prev => [...prev, {
          id: evData.message_id, role: 'assistant',
          content: evData.content,
          payload: evData.payload || (evData.content ? { block_type: 'text', block_data: { text: evData.content } } : null),
          status: 'complete', turn_id: evData.turn_id,
        }]);
      } else if (type === 'tool_message') {
        setMessages(prev => [...prev, {
          id: evData.message_id, role: 'tool', content: '',
          tool_name: evData.tool_name, status: evData.status,
          payload: { block_type: evData.block_type, block_data: evData.block_data },
          artifact_refs: evData.artifact_refs || [], turn_id: evData.turn_id,
        }]);
      } else if (type === 'agent.phase_started') {
        setAgentPhase(evData.label || evData.phase, evData.phase);
        trackCleanPhase(type, evData);
      } else if (type === 'agent.phase_completed') {
        trackCleanPhase(type, evData);
        setStepHistory(prev => {
          const trimmed = trimStepHistory(prev, evData.phase);
          if (trimmed.some(e => e.phase === evData.phase && e.timestamp === evData.timestamp)) return trimmed;
          return [...trimmed, { phase: evData.phase, label: evData.label, timestamp: evData.timestamp, detail: evData.detail || {} }];
        });
      } else if (type === 'message_complete') {
        setBusy(false);
        setAgentPhase(null);
      } else if (type === 'stream_disconnected') {
        setAgentStopped(true);
        setBusy(false);
        setAgentPhase(null);
        toast('Connection interrupted. Click Resume to continue.', 'info');
      } else if (type === 'error') {
        if (!HIDE_BRANDING && evData.code === 'output_token_limit_reached') toast(evData.message, 'error');
        setBusy(false);
      }
    }).catch(() => setBusy(false));
  }, [analysisId, refreshAnalysis, toast, setAgentPhase, trackCleanPhase]);

  // The clean-phase tracking belongs to one turn: once the agent is idle the
  // persisted block (always 100%) is the truth again.
  useEffect(() => {
    if (!busy) setCleanPhase(null);
  }, [busy]);

  const handleStop = useCallback(async () => {
    if (!analysisId) return;
    setAgentStopped(true);
    setBusy(false);
    try {
      // Sets analysis.agent_stopped; the background runner polls it and halts
      // at the next phase boundary (a tool already executing runs to its end).
      await stopAgentTurn(analysisId);
    } catch (e) {
      // Non-fatal — worst case the turn runs to completion.
    }
  }, [analysisId]);

  const handleResume = useCallback(() => {
    if (!analysisId || !agentStopped) return;
    setAgentStopped(false);
    handleSend('continue');
  }, [analysisId, agentStopped, handleSend]);

  // When the user returns to the tab, reconcile state in case anything was
  // missed while backgrounded. The OOB events channel reconnects on its own
  // (see subscribeAnalysisEvents) and the backend replays from Last-Event-ID,
  // but if the agent ran to completion while we were away the final messages
  // may already be persisted — refetch history + analysis to pick them up.
  useEffect(() => {
    if (!analysisId) return undefined;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const [analysisResp, chatResp] = await Promise.all([
          apiService.getAnalysis(analysisId),
          apiService.getAgentChatHistory(analysisId).catch(() => null),
        ]);
        if (analysisResp?.data) setAnalysis(analysisResp.data);
        if (chatResp?.data?.messages) setMessages(chatResp.data.messages);
        // Reconcile busy with the server's turn state: a turn may have
        // started (second tab) or finished while we were backgrounded.
        if (chatResp?.data) {
          if (chatResp.data.active_turn_id) {
            setBusy(true);
          } else if (reconnectingRef.current) {
            reconnectingRef.current = false;
            setBusy(false);
            setAgentPhase(null);
          }
        }
      } catch (_) {
        // Non-fatal — the next live event will catch us up.
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [analysisId, setAgentPhase]);

  // Drag-drop anywhere on the page.
  useEffect(() => {
    const onOver = (e) => { e.preventDefault(); setDragging(true); };
    const onLeave = (e) => { if (e.relatedTarget === null || e.clientX === 0) setDragging(false); };
    const onDrop = (e) => {
      e.preventDefault();
      setDragging(false);
      const fs = e.dataTransfer && e.dataTransfer.files;
      if (fs && fs.length) handleFileChosen(Array.from(fs));
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [handleFileChosen]);

  // Sidebar dataset groups: each "group" represents one preprocessed dataset
  // and its source raw uploads (raw -> preprocessed via merged_into). Orphan
  // raw uploads (no preprocessed result yet) become their own group so the
  // user can still view them.
  const datasetGroups = useMemo(() => {
    const all = analysis?.datasets || [];
    const preprocessed = all.filter(d => d.status !== 'raw');
    const rawByParent = new Map(); // preprocessed_id -> [raw...]
    const orphanRaw = [];
    for (const d of all) {
      if (d.status !== 'raw') continue;
      if (d.merged_into) {
        const arr = rawByParent.get(d.merged_into) || [];
        arr.push(d);
        rawByParent.set(d.merged_into, arr);
      } else {
        orphanRaw.push(d);
      }
    }

    const fmtSubtitle = (rows, cols, extra) => {
      const parts = [];
      if (rows != null && cols != null) {
        parts.push(`${Number(rows).toLocaleString()} rows · ${cols} cols`);
      }
      if (extra) parts.push(extra);
      return parts.join(' · ') || null;
    };

    const groups = preprocessed.map(d => {
      const raws = rawByParent.get(d.id) || [];
      const rawSuffix = raws.length === 0 ? null : `${raws.length} raw file${raws.length === 1 ? '' : 's'}`;
      return {
        id: `pre-${d.id}`,
        preprocessedId: d.id,
        name: d.name || `Dataset ${d.id}`,
        subtitle: fmtSubtitle(d.rows_count, d.columns_count, rawSuffix),
        preprocessed: d,
        rawUploads: raws,
        source: warehouseSource(raws),
      };
    });

    // Orphan raw uploads — each gets its own group so the user can still
    // open the raw spreadsheet before preprocessing finishes.
    for (const r of orphanRaw) {
      groups.push({
        id: `raw-${r.id}`,
        preprocessedId: null,
        name: r.original_filename || r.name || `Upload ${r.id}`,
        subtitle: fmtSubtitle(r.rows_count, r.columns_count, 'raw upload'),
        preprocessed: null,
        rawUploads: [r],
        source: warehouseSource([r]),
      });
    }

    return groups;
  }, [analysis]);

  // Shape consumed by ArtifactsRail (no `preprocessed` / full `rawUploads`
  // payload, just the bits needed to render rows + adornments).
  const datasets = useMemo(() => datasetGroups.map(g => ({
    id: g.id,
    name: g.name,
    subtitle: g.subtitle,
    preprocessedId: g.preprocessedId,
    rawUploads: g.rawUploads.map(r => ({ id: r.id })),
    source: g.source,
  })), [datasetGroups]);

  // The user's warehouse connections, for the rail's Sources section. Re-read
  // whenever the connect modal closes, since it can add or remove one.
  useEffect(() => {
    if (warehouseOpen) return;
    apiService.listConnections()
      .then((resp) => setConnections(resp.data.connections || []))
      .catch(() => setConnections([]));
  }, [warehouseOpen]);

  const openWarehouse = useCallback((connection) => {
    setWarehouseConnId(connection?.id ?? null);
    setWarehouseOpen(true);
  }, []);

  // "Manage pipeline" only makes sense when this analysis's data actually
  // came from a warehouse connector (schedules re-query that same source) —
  // not merely because the user has a connection configured elsewhere, and
  // not for analyses built from dropped-in files.
  const isWarehouseSourced = useMemo(
    () => (analysis?.datasets || []).some(
      (d) => d.status === 'raw' && d.profile_data?.warehouse_source,
    ),
    [analysis],
  );

  // Backend-computed: 'owner', or the AnalysisShare role ('view'/'edit'/'manage_pipeline')
  // for an analysis shared with the current user.
  const viewerRole = analysis?.viewer_role;
  const isOwner = viewerRole === 'owner';
  const canEdit = isNewAnalysis || viewerRole === 'owner' || viewerRole === 'edit' || viewerRole === 'manage_pipeline';
  const canManagePipeline = viewerRole === 'owner' || viewerRole === 'manage_pipeline';

  const activeViewerGroup = useMemo(() => {
    if (!viewerState) return null;
    return datasetGroups.find(g => g.id === viewerState.groupId) || null;
  }, [viewerState, datasetGroups]);

  const models = useMemo(() => {
    return (analysis?.models || []).map(m => ({
      id: m.id,
      name: m.target_column ? `${m.target_column}` : 'Model',
      // "QBrain" is a constant on purpose: it is the one model the product has,
      // and the engine underneath is never sent here (see _summarize_model_artifact).
      // No "full" badge either — preliminary models are gone, so the word carried
      // no information; the training date replaces it.
      subtitle: [
        'QBrain',
        formatMetric(m.test_metric_name, m.test_metric),
        formatTrainedOn(m.created_at),
      ].filter(Boolean).join(' · '),
    }));
  }, [analysis]);

  const predictions = useMemo(() => {
    return (analysis?.predictions || []).map(p => ({
      id: p.id,
      name: p.target_column ? `Predictions: ${p.target_column}` : 'Predictions',
      subtitle: p.n_predictions != null ? `${Number(p.n_predictions).toLocaleString()} rows` : null,
      downloadUrl: p.download_url || null,
    }));
  }, [analysis]);

  const handleRename = useCallback(async (newName) => {
    if (!analysisId || !newName.trim()) return;
    const prevName = analysis?.name;
    setAnalysis(prev => ({ ...prev, name: newName }));
    try {
      await apiService.updateAnalysis(analysisId, { name: newName });
    } catch (e) {
      toast(e?.response?.data?.detail || 'Could not rename analysis.', 'error');
      setAnalysis(prev => ({ ...prev, name: prevName }));
    }
  }, [analysisId, analysis?.name, toast]);

  const handleNewAnalysis = useCallback(async () => {
    try {
      const resp = await apiService.getAnalysisQuota();
      if (!resp.data.can_create) {
        toast(resp.data.detail || 'Analysis limit reached.', 'error');
        return;
      }
    } catch {
      // Non-fatal — if the quota check itself fails, let navigation proceed.
    }
    navigate('/app/analysis/new');
  }, [navigate, toast]);
  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleExportNotebook = useCallback(async () => {
    if (!analysisId || notebookExporting) return;
    setNotebookExporting(true);
    try {
      const resp = await apiService.exportNotebook(analysisId);
      const blob = new Blob([resp.data], { type: 'application/x-ipynb+json' });
      const cd = resp.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : `${(analysis?.name || 'analysis').replace(/[^A-Za-z0-9._-]+/g, '_')}.ipynb`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast('Could not export the notebook. Please try again.', 'error');
    } finally {
      setNotebookExporting(false);
    }
  }, [analysisId, notebookExporting, analysis, toast]);

  // Show next-step prompts when the most recent thing the user sees is the
  // dataset card — they've uploaded but haven't asked anything yet.
  const showNextStepSuggestions = useMemo(() => {
    if (busy) return false;
    if (!messages.length) return false;
    const last = messages[messages.length - 1];
    const blockType = last?.payload?.block_type;
    return blockType === 'dataset_summary' && (analysis?.models || []).length === 0;
  }, [busy, messages, analysis]);

  const isEmpty = !loading && messages.length === 0;

  return (
    <div className={cn('q-chat-shell', dragging && 'drag-over')}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xls,.xlsx,.json,.zip"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFileChosen(e.target.files ? Array.from(e.target.files) : []);
          e.target.value = '';
        }}
      />

      <ArtifactsRail
        datasets={datasets}
        models={models}
        predictions={predictions}
        threads={[]}
        onNewChat={handleNewAnalysis}
        newChatActive={isEmpty}
        connections={canEdit ? connections : []}
        onConnectSource={canEdit ? openWarehouse : undefined}
        onOpenDataset={({ groupId, initialView }) => setViewerState({ groupId, initialView })}
        activeDatasetId={viewerState?.groupId}
        onNavigateBack={() => navigate('/app')}
        onExportNotebook={analysisId && analysis?.notebook_exportable ? handleExportNotebook : undefined}
        notebookExporting={notebookExporting}
        onManagePipeline={analysisId && isWarehouseSourced && canManagePipeline ? (el) => setPipelineAnchor((prev) => (prev ? null : el)) : undefined}
      />

      <Popover
        open={!!pipelineAnchor}
        anchorEl={pipelineAnchor}
        onClose={() => setPipelineAnchor(null)}
        width={360}
      >
        <ManagePipelinePanel analysisId={analysisId} onClose={() => setPipelineAnchor(null)} />
      </Popover>

      <Popover
        open={!!shareAnchor}
        anchorEl={shareAnchor}
        onClose={() => setShareAnchor(null)}
        width={360}
      >
        <ShareAnalysisPanel analysisId={analysisId} analysis={analysis} onClose={() => setShareAnchor(null)} />
      </Popover>

      <main className="q-chat-main">
        <TopBar
          title={analysis?.name || 'Analysis'}
          onRename={analysisId && canEdit ? handleRename : undefined}
          onShare={analysisId && isOwner ? (el) => setShareAnchor((prev) => (prev ? null : el)) : undefined}
        />

        <div ref={scrollRef} className="q-chat-scroll">
          {isEmpty ? (
            <EmptyState>
              {canEdit && (
                <Composer
                  variant="center"
                  value={composerValue}
                  onChange={setComposerValue}
                  onSend={() => handleSend()}
                  attached={pendingFiles.map(p => ({ id: p.id, name: p.file.name, kind: 'dataset' }))}
                  onRemoveAttached={handleRemoveAttached}
                  onAttachClick={handleUploadClick}
                  onConnectSourceClick={() => openWarehouse(null)}
                  showSuggestions={false}
                  busy={busy}
                  attachPlaceholder={pendingFiles.length > 0
                    ? 'What would you like to predict? Also add relevant business context…'
                    : undefined}
                />
              )}
            </EmptyState>
          ) : (
            <div className="q-messages-wrap q-chat-thread">
              {messages.map((m, i) => {
                // Only the newest "Cleaning your data" block can be the live
                // one: an earlier one belongs to an earlier preprocessing run.
                const isLiveClean = busy
                  && m.payload?.block_type === 'preprocessing_progress'
                  && !messages.slice(i + 1).some(l => l.payload?.block_type === 'preprocessing_progress');
                if (isHiddenError(m.payload)) return null;
                const prevRole = prevShownRole(messages, i);
                const thisRole = m.role === 'user' ? 'user' : 'agent';
                const extraTop = prevRole !== null && prevRole !== thisRole ? 16 : 0;

                let inner;
                if (m.role === 'user') {
                  const atts = (m.payload?.attachments || []).map((a, j) => ({
                    id: a.dataset_id || `${m.id}-att-${j}`,
                    name: a.name, kind: 'dataset',
                  }));
                  inner = <UserMessage text={m.content} attached={atts} />;
                } else if (isTextOnly(m) && m.content) {
                  inner = (
                    <AgentMessage continued={prevRole === 'agent'}>
                      <div className="q-prose q-agent-card">
                        <TextBubble blockData={{ text: m.content }} />
                      </div>
                    </AgentMessage>
                  );
                } else if (m.payload && m.payload.block_type) {
                  // The phase-choice block keeps no record of the click, so it
                  // is told which phases ran after it (their result blocks
                  // appear later in the thread) and ticks the matching option.
                  const completed = m.payload.block_type === 'analysis_options'
                    ? messages.slice(i + 1).reduce((acc, later) => {
                      const t = later.payload?.block_type;
                      if (t === 'shap_summary') acc.explain = true;
                      if (t === 'deep_analysis_result') acc.insights = true;
                      return acc;
                    }, { explain: false, insights: false })
                    : undefined;
                  inner = (
                    <AgentMessage continued={prevRole === 'agent'}>
                      <BlockRenderer
                        blockType={m.payload.block_type}
                        blockData={m.payload.block_data}
                        status={m.status}
                        completed={completed}
                        live={isLiveClean ? (cleanPhase || { startedAt: null, done: false }) : null}
                        onSelectTarget={(col) => handleSend(`Train a preliminary model on ${col}.`)}
                        onTrainFullModel={(col) => handleSend(`Train the full model on ${col}.`)}
                        onSend={handleSend}
                        analysisId={analysisId}
                        // Fallback target for blocks persisted before the block
                        // payload carried one of its own (see DataAnalysis).
                        targetColumn={analysis?.confirmed_target || ''}
                      />
                    </AgentMessage>
                  );
                }

                if (!inner) return null;
                return (
                  <div key={m.id} className={extraTop ? 'q-turn' : undefined}>
                    {inner}
                  </div>
                );
              })}
              {showNextStepSuggestions && (
                <AgentMessage>
                  <div>
                    <div className="q-suggest-label">
                      Try asking
                    </div>
                    <div className="q-suggest-row">
                      <SuggestionChip
                        accent
                        label="What should I predict?"
                        onClick={() => handleSend('What should I predict from this dataset?')}
                      />
                      <SuggestionChip
                        label="Show key correlations"
                        onClick={() => handleSend('Show me the key correlations in this dataset.')}
                      />
                      <SuggestionChip
                        label="Summarize this data"
                        onClick={() => handleSend('Give me a summary of what is in this dataset.')}
                      />
                      <SuggestionChip
                        label="What patterns stand out?"
                        onClick={() => handleSend('What patterns or anomalies stand out in this data?')}
                      />
                    </div>
                  </div>
                </AgentMessage>
              )}
              {(busy || agentStopped) && (
                <div className={messages.length > 0 && messages[messages.length - 1].role === 'user' ? 'q-turn' : undefined}>
                  <AgentMessage
                    thinking
                    phase={agentPhase}
                    phaseId={agentPhaseId}
                    stepHistory={stepHistory}
                    agentStopped={agentStopped}
                    onStop={handleStop}
                    onResume={handleResume}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {!isEmpty && canEdit ? (
          <Composer
            value={composerValue}
            onChange={setComposerValue}
            onSend={() => handleSend()}
            attached={pendingFiles.map(p => ({ id: p.id, name: p.file.name, kind: 'dataset' }))}
            onRemoveAttached={handleRemoveAttached}
            onAttachClick={handleUploadClick}
            onConnectSourceClick={() => openWarehouse(null)}
            showSuggestions={false}
            busy={busy}
            attachPlaceholder={pendingFiles.length > 0
              ? 'What would you like to predict? Also add relevant business context…'
              : undefined}
          />
        ) : !canEdit ? (
          <div className="q-chat-readonly">
            You have view-only access to this shared analysis.
          </div>
        ) : null}
      </main>

      {activeViewerGroup && (
        <DatasetViewer
          preprocessed={activeViewerGroup.preprocessed}
          rawUploads={activeViewerGroup.rawUploads}
          initialView={viewerState.initialView}
          onClose={() => setViewerState(null)}
        />
      )}

      <TokenUsageModal
        isOpen={showCostModal}
        onClose={() => setShowCostModal(false)}
        tokenUsage={analysis?.token_usage}
      />

      <ConnectSourceModal
        open={warehouseOpen}
        initialConnectionId={warehouseConnId}
        onClose={() => setWarehouseOpen(false)}
        analysisId={analysisId}
        ensureAnalysisExists={ensureAnalysisExists}
        onImported={handleWarehouseImported}
        toast={toast}
      />
    </div>
  );
};

export default ChatAnalysisPage;
